import { approvalHistory } from "../../../mocks/approvals";

interface Props { approvalId: string | null; onResubmit?: (templateId: string, info: { title: string; aiComment: string; originalId: string }) => void; }

const statusCfg = {
  approved: { label: "최종 승인", badge: "text-[#16A34A] bg-[#F0FDF4] border-[#BBF7D0]", stampColor: "#16A34A", stampText: "承認", icon: "ri-checkbox-circle-fill", bannerBg: "from-[#F0FDF4] to-white", bannerBorder: "#BBF7D0", bannerText: "최종 승인 처리되었습니다" },
  rejected: { label: "반려", badge: "text-[#DC2626] bg-[#FFF5F5] border-[#FECACA]", stampColor: "#DC2626", stampText: "否決", icon: "ri-close-circle-fill", bannerBg: "from-[#FFF5F5] to-white", bannerBorder: "#FECACA", bannerText: "결재 반려 처리되었습니다" },
  pending: { label: "검토 중", badge: "text-[#D97706] bg-[#FFFBEB] border-[#FDE68A]", stampColor: "#D97706", stampText: "審査", icon: "ri-time-line", bannerBg: "from-[#FFFBEB] to-white", bannerBorder: "#FDE68A", bannerText: "검토 진행 중입니다" },
};

const typeIcons: Record<string, string> = { contract: "ri-file-paper-2-line", budget: "ri-money-dollar-circle-line", release: "ri-music-2-line", legal: "ri-scales-3-line" };
const typeLabels: Record<string, string> = { contract: "전속 계약서", budget: "예산 기안서", release: "발매 기안서", legal: "법적 대응 기안서" };

// 도장 컴포넌트
function ApprovalStamp({ status }: { status: string }) {
  const cfg = statusCfg[status as keyof typeof statusCfg] ?? statusCfg.pending;
  const isApproved = status === "approved";
  const isRejected = status === "rejected";
  return (
    <div className="relative">
      <div
        className={`w-20 h-20 rounded-full border-4 flex flex-col items-center justify-center shrink-0 ${isApproved ? "animate-[pulse_2s_ease-in-out_1]" : ""}`}
        style={{ borderColor: cfg.stampColor, opacity: isApproved ? 0.9 : isRejected ? 0.85 : 0.6, transform: "rotate(-12deg)", boxShadow: `0 0 0 2px ${cfg.stampColor}22` }}
      >
        <span className="text-2xl font-bold leading-none" style={{ color: cfg.stampColor, fontFamily: "serif" }}>{cfg.stampText}</span>
        <span className="text-[8px] font-medium mt-0.5" style={{ color: cfg.stampColor }}>K-ArtPEN</span>
      </div>
      {(isApproved || isRejected) && (
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: cfg.stampColor }}>
          <i className={`${isApproved ? "ri-check-line" : "ri-close-line"} text-white text-[10px]`}></i>
        </div>
      )}
    </div>
  );
}

const typeToTemplate: Record<string, string> = { contract: "tmpl1", budget: "tmpl2", release: "tmpl3", legal: "tmpl4" };

export default function ApprovalDetail({ approvalId, onResubmit }: Props) {
  const approval = approvalHistory.find(a => a.id === approvalId);

  if (!approval) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center bg-[#E8E8E8]">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/60 mb-4 shadow-sm">
          <i className="ri-file-list-3-line text-3xl text-[#CCCCCC]"></i>
        </div>
        <p className="text-[#AAAAAA] text-sm">결재 항목을 선택하거나</p>
        <p className="text-[#AAAAAA] text-sm">새 기안서를 작성하세요</p>
        <p className="text-[#DDDDDD] text-xs mt-3">좌측 패널에서 양식을 선택하거나<br />결재 이력을 클릭해 열람하세요</p>
      </div>
    );
  }

  const cfg = statusCfg[approval.status as keyof typeof statusCfg] ?? statusCfg.pending;
  const today = new Date(approval.submittedAt).toLocaleDateString("ko-KR") ?? approval.submittedAt;
  const isApproved = approval.status === "approved";
  const isRejected = approval.status === "rejected";

  return (
    <div className="flex flex-col h-full bg-[#E8E8E8] overflow-y-auto">
      {/* 상태 배너 */}
      <div
        className={`px-5 py-3.5 border-b flex items-center gap-3 bg-gradient-to-r ${cfg.bannerBg}`}
        style={{ borderBottomColor: cfg.bannerBorder, borderBottomWidth: "1.5px" }}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ backgroundColor: cfg.stampColor + "18" }}>
          <i className={`${cfg.icon} text-base`} style={{ color: cfg.stampColor }}></i>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold" style={{ color: cfg.stampColor }}>{cfg.bannerText}</span>
            {approval.status !== "pending" && (
              <span className="text-[10px] text-[#AAAAAA]">{approval.submittedAt}</span>
            )}
          </div>
          <p className="text-[11px] text-[#777777] mt-0.5 truncate">{approval.title}</p>
        </div>
        <span className={`text-[10px] px-2.5 py-1 rounded-full border font-bold shrink-0 ${cfg.badge}`}>
          <i className={`${cfg.icon} mr-1`}></i>{cfg.label}
        </span>
      </div>

      {/* 문서 본체 */}
      <div className="flex-1 py-8 px-8">
        <div className="max-w-3xl mx-auto bg-white shadow-sm" style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)", borderTop: `3px solid ${cfg.stampColor}` }}>
          {/* 문서 상태 워터마크 배경 */}
          {(isApproved || isRejected) && (
            <div className="absolute pointer-events-none inset-0 flex items-center justify-center opacity-[0.025]" aria-hidden>
              <span className="text-[200px] font-black rotate-[-30deg] select-none" style={{ color: cfg.stampColor }}>
                {isApproved ? "承認" : "否決"}
              </span>
            </div>
          )}
          <div className="px-14 py-12 relative" style={{ fontFamily: "'Noto Serif KR', 'Pretendard Variable', serif" }}>

            {/* 헤더 */}
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="text-[11px] text-[#555] font-bold tracking-widest mb-1">K-ArtPEN ENTERTAINMENT</div>
                <table className="text-[10px] text-[#555] border-collapse">
                  <tbody>
                    {[["기안자", approval.submittedBy], ["기안일자", approval.submittedAt], ["문서유형", typeLabels[approval.type] ?? "기안서"]].map(([k, v]) => (
                      <tr key={k}><td className="pr-3 font-medium text-[#888] py-0.5">{k}</td><td className="text-[#444]">{v}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* 결재란 + 도장 */}
              <div className="flex items-start gap-4">
                <ApprovalStamp status={approval.status} />
                <table className="border-collapse text-[10px]">
                  <tbody>
                    <tr>
                      {["기안", "검토", "결재"].map(l => <td key={l} className="text-center font-semibold bg-[#F2F2F2] px-4 py-1" style={{ border: "1px solid #999", minWidth: 56 }}>{l}</td>)}
                    </tr>
                    <tr>
                      <td className="text-center px-2 py-3 align-top" style={{ border: "1px solid #DDD", height: 56 }}>
                        <div className="text-[9px] text-[#888] mb-1">{approval.submittedBy.split(" ")[0]}</div>
                        <div className="text-[9px] text-[#333]">(서명)</div>
                      </td>
                      <td className="text-center px-2 py-3 align-top" style={{ border: "1px solid #DDD" }}>
                        <div className="text-[9px] text-[#888] mb-1">재무팀</div>
                        {approval.status !== "pending" && <div className="text-[9px] text-[#059669] font-bold">✓</div>}
                      </td>
                      <td className="text-center px-2 py-3 align-top" style={{ border: "1px solid #DDD" }}>
                        <div className="text-[9px] text-[#888] mb-1">대표이사</div>
                        {isApproved && <div className="text-[9px] text-[#16A34A] font-bold">✓</div>}
                        {isRejected && <div className="text-[9px] text-[#DC2626] font-bold">✗</div>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t-2 border-[#1A1A1A] mt-4 mb-2"></div>
            <div className="border-t border-[#CCCCCC] mb-8"></div>

            <div className="text-center mb-2">
              <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-wider" style={{ letterSpacing: "0.15em" }}>
                {(typeLabels[approval.type] ?? "기안서").replace(/\s/g, "\u3000")}
              </h1>
            </div>
            <div className="border-b-2 border-[#1A1A1A] mb-1 mx-auto" style={{ width: "60%" }}></div>
            <div className="border-b border-[#999] mb-10 mx-auto" style={{ width: "60%" }}></div>

            {/* 문서 요약 테이블 */}
            <table className="w-full border-collapse text-sm mb-6">
              <tbody>
                <tr className="bg-[#F9F9F9]">
                  <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#888] text-xs font-semibold w-28">문서 제목</td>
                  <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#1A1A1A] font-medium" colSpan={3}>{approval.title}</td>
                </tr>
                <tr>
                  <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#888] text-xs font-semibold">기안자</td>
                  <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#1A1A1A] text-sm">{approval.submittedBy}</td>
                  <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#888] text-xs font-semibold w-28">기안일</td>
                  <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#1A1A1A] text-sm">{approval.submittedAt}</td>
                </tr>
                {approval.amount > 0 && (
                  <tr className="bg-[#F9F9F9]">
                    <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#888] text-xs font-semibold">집행 금액</td>
                    <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#1A1A1A] font-semibold">{(approval.amount / 10000).toLocaleString()}만원</td>
                    <td className="border border-[#E0E0E0] px-4 py-2.5 text-[#888] text-xs font-semibold">처리 결과</td>
                    <td className={`border border-[#E0E0E0] px-4 py-2.5 font-bold text-sm ${isApproved ? "text-[#16A34A]" : isRejected ? "text-[#DC2626]" : "text-[#D97706]"}`}>
                      {cfg.label}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* 영향도 */}
            {(approval.reputationChange !== 0 || approval.capitalChange !== 0) && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: "명성 변화", value: `${approval.reputationChange > 0 ? "+" : ""}${approval.reputationChange}pt`, pos: approval.reputationChange >= 0 },
                  { label: "자본금 변화", value: `${approval.capitalChange > 0 ? "+" : ""}${approval.capitalChange.toLocaleString()}만원`, pos: approval.capitalChange >= 0 },
                ].map(m => (
                  <div key={m.label} className="border border-[#E0E0E0] rounded p-3 text-center bg-[#FAFAFA]">
                    <div className="text-[10px] text-[#888] mb-1">{m.label}</div>
                    <div className={`text-base font-bold ${m.pos ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{m.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* 결재 상태 하이라이트 박스 */}
            {(isApproved || isRejected) && (
              <div className={`mb-6 p-4 rounded-lg border-l-4 ${isApproved ? "bg-[#F0FDF4] border-l-[#16A34A]" : "bg-[#FFF5F5] border-l-[#DC2626]"}`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isApproved ? "bg-[#16A34A]" : "bg-[#DC2626]"}`}>
                    <i className={`${isApproved ? "ri-check-double-line" : "ri-close-line"} text-white text-base`}></i>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${isApproved ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                      {isApproved ? "최종 승인 완료" : "결재 반려"}
                    </p>
                    <p className="text-xs text-[#666666] mt-0.5">
                      {isApproved ? "경영 지표에 반영됩니다. 담당 팀에 후속 조치를 요청하세요." : "반려 사유를 확인하고 수정 후 재제출하세요."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* AI 검토 의견 */}
            <div className={`p-4 rounded border mb-6 ${isApproved ? "bg-[#F0FDF4] border-[#BBF7D0]" : isRejected ? "bg-[#FFF5F5] border-[#FECACA]" : "bg-[#FFFBEB] border-[#FDE68A]"}`}>
              <div className="flex items-start gap-2 mb-1.5">
                <i className="ri-robot-line text-sm text-[#555] mt-0.5"></i>
                <span className="text-xs font-bold text-[#333]">AI 법무 시스템 검토 의견</span>
              </div>
              <p className="text-xs text-[#555] leading-relaxed">{approval.aiComment}</p>
            </div>

            {/* 결재 타임라인 */}
            <div className="border-t border-[#E0E0E0] pt-5">
              <p className="text-[10px] text-[#888] font-semibold uppercase tracking-widest mb-4">결재 처리 현황</p>
              <div className="flex items-center gap-0">
                {[
                  { label: "기안 제출", who: approval.submittedBy, done: true, color: "#1A1A1A" },
                  { label: "재무팀 검토", who: "재무담당", done: approval.status !== "pending", color: approval.status !== "pending" ? "#059669" : "#DDD" },
                  { label: "대표이사 최종결재", who: "대표이사", done: approval.status === "approved" || approval.status === "rejected", color: isApproved ? "#16A34A" : isRejected ? "#DC2626" : "#DDD" },
                ].map((step, i, arr) => (
                  <div key={step.label} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold transition-all ${step.done ? "text-white" : "text-[#CCC] border-[#DDD] bg-white"}`}
                        style={step.done ? { backgroundColor: step.color, borderColor: step.color } : {}}>
                        {step.done ? <i className="ri-check-line"></i> : i + 1}
                      </div>
                      <div className="text-[9px] text-[#888] mt-1.5 text-center whitespace-nowrap">{step.label}</div>
                      <div className="text-[8px] text-[#BBBBBB] mt-0.5 text-center">{step.who}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="w-16 h-0.5 mx-2 mb-5 rounded-full transition-all" style={{ backgroundColor: arr[i + 1].done ? step.color : "#E0E0E0" }}></div>
                    )}
                  </div>
                ))}
              </div>

              {/* 반려 안내 + 재기안 버튼 */}
              {isRejected && (
                <div className="mt-4 bg-[#FFF5F5] border border-[#FECACA] rounded-lg px-4 py-3">
                  <div className="flex items-start gap-2.5 mb-3">
                    <i className="ri-refresh-line text-[#DC2626] text-sm shrink-0 mt-0.5"></i>
                    <p className="text-xs text-[#DC2626]">반려된 기안서는 내용 수정 후 재제출할 수 있습니다. AI 검토 의견을 참고해 필수 항목을 보완하세요.</p>
                  </div>
                  {onResubmit && (
                    <button
                      type="button"
                      onClick={() => onResubmit(typeToTemplate[approval.type] ?? "tmpl2", { title: approval.title, aiComment: approval.aiComment, originalId: approvalId ?? "" })}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-edit-line text-sm"></i>수정하여 재기안하기
                    </button>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
